import { AxiosInstance } from "./Axios";

class NewsService {
  static async getNewsData() {
    try {
      const response = await AxiosInstance.get("news-proxy/latest-results");
      return response.data;
    } catch (error) {
      console.error("Error fetching news data:", error);
      throw error;
    }
  }

  static async getNewsByTag(tag) {
    try {
      const response = await AxiosInstance.get("news-proxy/api/news", {
        params: { tag },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching news by tag:", error);
      throw error;
    }
  }

  static async getRelevantNews() {
    try {
      const response = await AxiosInstance.get("news-proxy/api/news", {
        params: { relevant: true },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching relevant news:", error);
      throw error;
    }
  }

  static async searchNews(query) {
    try {
      const response = await AxiosInstance.get("news-proxy/api/news/search", {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.error("Error searching news:", error);
      throw error;
    }
  }
}

export async function defaultPrompt() {
  try {
    let response = await AxiosInstance.get("news-proxy/default_prompt");
    return response?.data;
  } catch (error) {
    console.error(`Error defaultPrompt: ${error}`);
    throw error;
  }
}

export async function listOfLanguages() {
  try {
    let response = await AxiosInstance.get("news-proxy/list_of_languages");
    return response?.data;
  } catch (error) {
    console.error(`Error listOfLanguages: ${error}`);
    throw error;
  }
}

export async function listOfAnalysts() {
  try {
    let response = await AxiosInstance.get("news-proxy/list_of_analysts");
    return response?.data;
  } catch (error) {
    console.error(`Error listOfAnalysts: ${error}`);
    throw error;
  }
}

// Keywords Management API functions
export async function fetchKeywords(analyst = null) {
  try {
    const params = new URLSearchParams();
    if (analyst) params.append("analyst", analyst);
    const url = params.toString()
      ? `news-proxy/keywords?${params.toString()}`
      : "news-proxy/keywords";
    let response = await AxiosInstance.get(url);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchKeywords: ${error}`);
    throw error;
  }
}

export async function fetchKeywordById(id) {
  try {
    console.log("Fetching keyword with ID:", id);
    let response = await AxiosInstance.get(`news-proxy/keywords/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchKeywordById: ${error}`);
    console.error("ID that failed:", id);
    throw error;
  }
}

export async function createKeyword(keywordData) {
  try {
    let response = await AxiosInstance.post("news-proxy/keywords", keywordData);
    return response?.data;
  } catch (error) {
    console.error(`Error createKeyword: ${error}`);
    throw error;
  }
}

export async function updateKeyword(id, keywordData) {
  try {
    let response = await AxiosInstance.put(
      `news-proxy/keywords/${id}`,
      keywordData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error updateKeyword: ${error}`);
    throw error;
  }
}

export async function deleteKeyword(id) {
  try {
    let response = await AxiosInstance.delete(`news-proxy/keywords/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteKeyword: ${error}`);
    throw error;
  }
}

export async function copyKeyword(id, newTag) {
  try {
    let response = await AxiosInstance.post(
      `news-proxy/keywords/${id}/copy`,
      null,
      { params: { new_tag: newTag } },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error copyKeyword: ${error}`);
    throw error;
  }
}

export async function replaceKeyword(id, keywordData) {
  try {
    let response = await AxiosInstance.post(
      `news-proxy/keywords/${id}/replace`,
      keywordData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error replaceKeyword: ${error}`);
    throw error;
  }
}

export async function getCompanyInfo(targetTicker, buyerTicker) {
  try {
    const params = new URLSearchParams();
    if (targetTicker) params.append("target_ticker", targetTicker);
    if (buyerTicker && buyerTicker.trim())
      params.append("buyer_ticker", buyerTicker);

    let response = await AxiosInstance.get(
      `news-proxy/company-info?${params.toString()}`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getCompanyInfo: ${error}`);
    throw error;
  }
}

// Feedback API functions
export async function submitFeedback(feedbackData) {
  try {
    let response = await AxiosInstance.post(
      "news-proxy/feedback",
      feedbackData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error submitFeedback: ${error}`);
    throw error;
  }
}

export async function upsertFeedback(feedbackData) {
  try {
    let response = await AxiosInstance.post(
      "news-proxy/feedback/upsert",
      feedbackData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error upsertFeedback: ${error}`);
    throw error;
  }
}

export async function getFeedback(tag = null, headlineUniqueId = null) {
  try {
    const params = new URLSearchParams();
    if (tag) params.append("tag", tag);
    if (headlineUniqueId) params.append("headline_unique_id", headlineUniqueId);

    let response = await AxiosInstance.get(
      `news-proxy/feedback?${params.toString()}`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getFeedback: ${error}`);
    throw error;
  }
}

export async function getFeedbackById(id) {
  try {
    let response = await AxiosInstance.get(`news-proxy/feedback/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error getFeedbackById: ${error}`);
    throw error;
  }
}

export async function deleteFeedback(id) {
  try {
    let response = await AxiosInstance.delete(`news-proxy/feedback/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteFeedback: ${error}`);
    throw error;
  }
}

export async function updateFeedback(id, feedbackData) {
  try {
    let response = await AxiosInstance.put(
      `news-proxy/feedback/${id}`,
      feedbackData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error updateFeedback: ${error}`);
    throw error;
  }
}

export async function getFeedbackPaginated(
  page = 1,
  pageSize = 50,
  search = null,
  tag = null,
  feedback = null,
  analyst = null,
) {
  try {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("page_size", pageSize);
    if (search) params.append("search", search);
    if (tag) params.append("tag", tag);
    if (feedback !== null) params.append("feedback", feedback);
    if (analyst) params.append("analyst", analyst);

    let response = await AxiosInstance.get(
      `news-proxy/feedback/paginated?${params.toString()}`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getFeedbackPaginated: ${error}`);
    throw error;
  }
}

// Manual runs API functions
export async function getTagsWithLastRun() {
  try {
    let response = await AxiosInstance.get(
      "news-proxy/manual-runs/tags-with-last-run",
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getTagsWithLastRun: ${error}`);
    throw error;
  }
}

export async function runManualTags(tagIds, passphrase, daysBack = 3) {
  try {
    let response = await AxiosInstance.post("news-proxy/manual-runs/run-tags", {
      tag_ids: tagIds,
      days_back: daysBack,
      passphrase: passphrase,
    });
    return response?.data;
  } catch (error) {
    console.error(`Error runManualTags: ${error}`);
    throw error;
  }
}

export async function processNewsPipeline() {
  try {
    let response = await AxiosInstance.post("news-proxy/process-news-pipeline");
    return response?.data;
  } catch (error) {
    console.error(`Error processNewsPipeline: ${error}`);
    throw error;
  }
}

export async function runLLMDeduplicationOnly(
  tagIds,
  passphrase,
  daysBack = 3,
) {
  try {
    let response = await AxiosInstance.post(
      "news-proxy/manual-runs/llm-deduplication-only",
      {
        tag_ids: tagIds,
        days_back: daysBack,
        passphrase: passphrase,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error runLLMDeduplicationOnly: ${error}`);
    throw error;
  }
}

export default NewsService;
